// Renders all seats of one level as a single SVG <g>. Geometry is memoized so
// React only re-renders when the seat array reference changes. Hover, cursor,
// and selection feedback is CSS-driven (rect.seat selectors in globals.css) —
// no React state per seat.

import { type SeatDto, isGroundSpecialZoneSeat } from '@okn/shared';
import { memo, useMemo } from 'react';
import { STATE_FILL_VAR, type SeatStateContext, resolveSeatState } from './seat-state.ts';

type Props = {
  seats: SeatDto[];
  stateCtx: SeatStateContext;
  selectedIds: Set<string>;
  onSeatClick?: (seat: SeatDto) => void;
  /** seat side length in level-local units */
  seatSize?: number;
};

export const SeatLayer = memo(function SeatLayer({
  seats,
  stateCtx,
  selectedIds,
  onSeatClick,
  seatSize = 10,
}: Props) {
  const onClick = (e: React.MouseEvent<SVGGElement>) => {
    if (!onSeatClick) return;
    let el = e.target as Element | null;
    while (el && el !== e.currentTarget) {
      const sid = (el as HTMLElement).dataset?.seatId;
      if (sid) {
        const seat = seats.find((s) => s.id === sid);
        if (seat) onSeatClick(seat);
        return;
      }
      el = el.parentElement;
    }
  };

  // Row labels — one per row, placed just before the first seat on the LEFT
  // edge of the row. Helps orientation when zoomed out.
  const rowLabels = useMemo(() => {
    const byRow = new Map<string, SeatDto[]>();
    for (const s of seats) {
      const arr = byRow.get(s.rowLabel) ?? [];
      arr.push(s);
      byRow.set(s.rowLabel, arr);
    }
    return Array.from(byRow.entries()).map(([row, items]) => {
      let leftmost = items[0]!;
      for (const s of items) {
        if (s.posX < leftmost.posX) leftmost = s;
      }
      return {
        row,
        x: leftmost.posX - seatSize * 1.6,
        y: leftmost.posY + seatSize * 0.35,
      };
    });
  }, [seats, seatSize]);

  const rects = useMemo(() => {
    const half = seatSize / 2;
    return seats.map((s) => {
      const state = resolveSeatState(s, stateCtx);
      const zone =
        s.level === 'ground' &&
        s.kind === 'standard' &&
        s.isSellable &&
        isGroundSpecialZoneSeat(s.rowLabel, s.seatNumber);
      return {
        id: s.id,
        x: s.posX - half,
        y: s.posY - half,
        cx: s.posX,
        cy: s.posY,
        num: s.seatNumber,
        fill: STATE_FILL_VAR[state],
        state,
        selected: selectedIds.has(s.id),
        kind: s.kind,
        sellable: s.isSellable,
        zone,
        label: s.label,
      };
    });
  }, [seats, stateCtx, selectedIds, seatSize]);

  // Horizontal walking aisles read as an above-typical vertical gap between two
  // adjacent rows. Detect them from geometry (no DTO flag needed) so we can draw
  // a "KORİDOR" divider exactly where the layout left a gap.
  const corridors = useMemo(() => {
    const ys = Array.from(new Set(seats.map((s) => s.posY))).sort((a, b) => a - b);
    if (ys.length < 3) return [] as Array<{ y: number; x1: number; x2: number }>;
    const diffs: number[] = [];
    for (let i = 1; i < ys.length; i++) diffs.push(ys[i]! - ys[i - 1]!);
    const median = [...diffs].sort((a, b) => a - b)[Math.floor(diffs.length / 2)]!;
    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    for (const s of seats) {
      if (s.posX < minX) minX = s.posX;
      if (s.posX > maxX) maxX = s.posX;
    }
    const out: Array<{ y: number; x1: number; x2: number }> = [];
    for (let i = 1; i < ys.length; i++) {
      if (ys[i]! - ys[i - 1]! > median * 1.5) {
        out.push({ y: (ys[i]! + ys[i - 1]!) / 2, x1: minX - seatSize, x2: maxX + seatSize });
      }
    }
    return out;
  }, [seats, seatSize]);

  const showNumbers = seatSize >= 10;

  return (
    <g onClick={onClick} aria-label="Koltuk haritası">
      {corridors.map((c) => (
        <g key={`cor-${c.y}`} pointerEvents="none" aria-hidden>
          <line
            x1={c.x1}
            y1={c.y}
            x2={c.x2}
            y2={c.y}
            stroke="var(--border-strong)"
            strokeWidth={0.6}
            strokeDasharray="3 4"
          />
          <text
            x={(c.x1 + c.x2) / 2}
            y={c.y + seatSize * 0.42}
            textAnchor="middle"
            fontSize={seatSize * 1.05}
            fill="var(--text-faint)"
            style={{ letterSpacing: '0.42em', userSelect: 'none' }}
          >
            KORİDOR
          </text>
        </g>
      ))}
      {rowLabels.map((rl) => (
        <text
          key={`rl-${rl.row}`}
          x={rl.x}
          y={rl.y}
          fontSize={seatSize * 0.85}
          fill="var(--text-faint)"
          textAnchor="end"
          pointerEvents="none"
          style={{ userSelect: 'none' }}
        >
          {rl.row}
        </text>
      ))}
      {rects.map((r) => (
        <rect
          key={r.id}
          data-seat-id={r.id}
          data-seat-kind={r.kind}
          data-seat-sellable={r.sellable ? '1' : '0'}
          data-state={r.state}
          data-selected={r.selected ? '1' : '0'}
          data-zone={r.zone ? '1' : '0'}
          x={r.x}
          y={r.y}
          width={seatSize}
          height={seatSize}
          rx={2}
          ry={2}
          fill={r.fill}
          // Selected = your current pick. Inline style.fill beats the zone/hover
          // CSS rules (presentation `fill` attr would lose to them), so the pick
          // always shows the bright accent + a contrasting ring — never gray.
          style={r.selected ? { fill: 'var(--seat-selected)' } : undefined}
          stroke={r.selected ? 'var(--seat-selected-ring)' : 'transparent'}
          strokeWidth={r.selected ? 1.8 : 0}
          aria-label={r.label}
          aria-pressed={r.selected}
          className="seat"
        >
          <title>{r.label}</title>
        </rect>
      ))}
      {showNumbers && (
        <g pointerEvents="none" aria-hidden>
          {rects.map((r) => (
            <text
              key={`n-${r.id}`}
              x={r.cx}
              y={r.cy}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={seatSize * 0.5}
              fill="var(--seat-number)"
              style={{ userSelect: 'none', fontVariantNumeric: 'tabular-nums' }}
            >
              {r.num}
            </text>
          ))}
        </g>
      )}
    </g>
  );
});
